import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getPathWithRef } from '../utils/attribution';
import { mvpApi } from '../services/api';
import { showPopupMessage } from '../utils/showPopup';
import LanguageSwitch from '../components/LanguageSwitch';
import BrandLockup from '../components/BrandLockup';
import { SkeletonModal } from '../components/advisor/SkeletonLoader';

/** Native <option> text only — include email/phone so duplicate names are distinguishable. */
function formatRegistrationSelectLabel(u) {
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  const phone = (u.phone || '').trim();
  const role = u.role || 'user';
  const segments = [];
  if (name) segments.push(name);
  if (email) segments.push(email);
  if (phone) segments.push(phone);
  if (segments.length === 0) segments.push(u.id != null ? `User #${u.id}` : 'Unknown');
  const idHint = !email && !phone && u.id != null ? ` · #${u.id}` : '';
  return `${segments.join(' · ')}${idHint} — ${role}`;
}

function formatAdvisorSelectLabel(a) {
  const name = (a.name || '').trim();
  const email = (a.email || '').trim();
  const phone = (a.phone || '').trim();
  const segments = [];
  if (name) segments.push(name);
  if (email) segments.push(email);
  if (phone) segments.push(phone);
  if (segments.length === 0) segments.push(a.id != null ? `Advisor #${a.id}` : 'Advisor');
  const idHint = !email && !phone && a.id != null ? ` · #${a.id}` : '';
  return `${segments.join(' · ')}${idHint}`;
}

/** Custom searchable dropdown that opens on click and contains a search bar. */
function SearchablePicker({
  options,
  value,
  onChange,
  placeholder,
  labelFunc,
  searchPlaceholder,
  noResultsText
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const selectedOption = options.find(o => String(o.id) === String(value));

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const filtered = options.filter(o =>
    labelFunc(o).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative w-full mb-3" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setQuery('');
        }}
        className="w-full flex items-center justify-between px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all shadow-sm"
      >
        <span className="truncate mr-2 font-medium">
          {selectedOption ? labelFunc(selectedOption) : placeholder}
        </span>
        <i className={`ri-arrow-down-s-line text-slate-400 text-lg transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="relative z-10 mt-1 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden flex flex-col min-w-0 max-h-60 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-y-auto overflow-x-hidden flex-1 divide-y divide-slate-50 scrollbar-thin scrollbar-thumb-slate-200">
            {filtered.length === 0 ? (
              <p className="p-4 text-xs text-slate-500 text-center">{noResultsText || 'No results'}</p>
            ) : (
              filtered.map((o) => {
                const isSel = String(value) === String(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-[13px] flex items-center justify-between gap-3 transition-colors ${isSel ? 'bg-emerald-50 text-emerald-900 font-medium' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    onClick={() => {
                      onChange(o.id);
                      setIsOpen(false);
                    }}
                  >
                    <span className="truncate">{labelFunc(o)}</span>
                    {isSel && <i className="ri-check-line text-emerald-600 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdvisorTopNav({ advisorName = 'Advisor' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [advisorsOpen, setAdvisorsOpen] = useState(false);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [advisorsList, setAdvisorsList] = useState([]);
  const [advisorsError, setAdvisorsError] = useState(null);
  const [currentAdvisorUserId, setCurrentAdvisorUserId] = useState(null);
  const [deletingAdvisorId, setDeletingAdvisorId] = useState(null);

  const [portalOpen, setPortalOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalErr, setPortalErr] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [advisorsForSelect, setAdvisorsForSelect] = useState([]);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [defaultAdvisorSelect, setDefaultAdvisorSelect] = useState('');
  const [bulkAdvisorSelect, setBulkAdvisorSelect] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const [portalSaving, setPortalSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [roleFeedback, setRoleFeedback] = useState(null);
  const [portalFeedback, setPortalFeedback] = useState(null);
  const [bulkFeedback, setBulkFeedback] = useState(null);

  const wrapRef = useRef(null);

  const initials = advisorName
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    closeMenu();
    navigate(getPathWithRef('/login'));
  }, [navigate, closeMenu]);

  const openAdvisorsModal = useCallback(async () => {
    closeMenu();
    setAdvisorsOpen(true);
    setAdvisorsError(null);
    setAdvisorsLoading(true);
    setCurrentAdvisorUserId(null);
    try {
      const [advRes, meRes] = await Promise.all([mvpApi.getAdminAdvisors(), mvpApi.getMe()]);
      setAdvisorsList(advRes.data?.advisors || []);
      const me = meRes.data?.user || meRes.data;
      setCurrentAdvisorUserId(me?.id != null && me?.id !== '' ? Number(me.id) : null);
    } catch (e) {
      setAdvisorsError(t('advisorPortal.topNavCouldNotLoadAdvisors'));
      setAdvisorsList([]);
      setCurrentAdvisorUserId(null);
    } finally {
      setAdvisorsLoading(false);
    }
  }, [closeMenu, t]);

  const closeAdvisorsModal = useCallback(() => {
    setAdvisorsOpen(false);
    setDeletingAdvisorId(null);
  }, []);

  const handleDeleteAdvisor = useCallback(
    async (a) => {
      if (!a?.id) return;
      if (currentAdvisorUserId != null && Number(a.id) === Number(currentAdvisorUserId)) return;
      const label = a.name || a.email || `User ${a.id}`;
      const ok = window.confirm(t('advisorPortal.topNavDeleteAdvisorConfirm', { label }));
      if (!ok) return;
      setDeletingAdvisorId(a.id);
      try {
        await mvpApi.deleteAdminUser(a.id);
        setAdvisorsList((prev) => prev.filter((x) => Number(x.id) !== Number(a.id)));
      } catch (e) {
        const d = e.response?.data?.detail;
        showPopupMessage(typeof d === 'string' ? d : t('advisorPortal.topNavDeleteAdvisorFailed'), 'error');
      } finally {
        setDeletingAdvisorId(null);
      }
    },
    [currentAdvisorUserId, t],
  );

  const closePortalModal = useCallback(() => {
    setPortalOpen(false);
    setRoleSearchQuery('');
    setSelectedUserId('');
    setRoleFeedback(null);
    setPortalFeedback(null);
    setBulkFeedback(null);
    setPortalErr(null);
  }, []);

  const openPortalModal = useCallback(async () => {
    closeMenu();
    setPortalOpen(true);
    setPortalLoading(true);
    setPortalErr(null);
    setRoleFeedback(null);
    setPortalFeedback(null);
    setBulkFeedback(null);
    try {
      const [regRes, advRes, settingsRes] = await Promise.all([
        mvpApi.getAdminRegistrations({ page: 1, page_size: 500 }),
        mvpApi.getAdminAdvisors(),
        mvpApi.getPortalSettings(),
      ]);
      const users = regRes.data?.users || [];
      const advs = advRes.data?.advisors || [];
      setRegistrations(users);
      setAdvisorsForSelect(advs);
      const ps = settingsRes.data;
      const defId = ps?.default_advisor_user_id;
      setDefaultAdvisorSelect(defId != null ? String(defId) : '');
      setBulkAdvisorSelect(advs[0]?.id != null ? String(advs[0].id) : '');
    } catch (e) {
      setPortalErr(t('advisorPortal.topNavCouldNotLoadPortal'));
      setRegistrations([]);
      setAdvisorsForSelect([]);
    } finally {
      setPortalLoading(false);
    }
  }, [closeMenu, t]);

  const filteredRegistrations = useMemo(() => {
    const q = roleSearchQuery.toLowerCase().trim();
    if (!q) return registrations;
    return registrations.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        String(u.phone || '').toLowerCase().includes(q) ||
        String(u.id || '').includes(q),
    );
  }, [registrations, roleSearchQuery]);

  const selectedUser = useMemo(
    () => registrations.find((u) => String(u.id) === String(selectedUserId)),
    [registrations, selectedUserId],
  );

  const applyRole = useCallback(
    async (role) => {
      if (!selectedUserId) return;
      setRoleSaving(true);
      setRoleFeedback(null);
      try {
        await mvpApi.patchAdminUserRole(Number(selectedUserId), role);
        const reg = await mvpApi.getAdminRegistrations({ page: 1, page_size: 500 });
        setRegistrations(reg.data?.users || []);
        setRoleFeedback(t('advisorPortal.topNavRoleUpdated'));
      } catch (e) {
        const d = e.response?.data?.detail;
        setRoleFeedback(typeof d === 'string' ? d : t('advisorPortal.topNavCouldNotUpdateRole'));
      } finally {
        setRoleSaving(false);
      }
    },
    [selectedUserId, t],
  );

  const saveDefaultAdvisor = useCallback(async () => {
    setPortalSaving(true);
    setPortalFeedback(null);
    try {
      await mvpApi.putPortalSettings({
        default_advisor_user_id: defaultAdvisorSelect === '' ? null : Number(defaultAdvisorSelect),
      });
      setPortalFeedback(t('advisorPortal.topNavDefaultAdvisorSaved'));
    } catch (e) {
      const d = e.response?.data?.detail;
      setPortalFeedback(typeof d === 'string' ? d : t('advisorPortal.topNavCouldNotSaveSettings'));
    } finally {
      setPortalSaving(false);
    }
  }, [defaultAdvisorSelect, t]);

  const runBulkAssign = useCallback(async () => {
    if (!bulkAdvisorSelect) {
      setBulkFeedback(t('advisorPortal.topNavChooseAdvisorFirst'));
      return;
    }
    const ok = window.confirm(t('advisorPortal.topNavBulkConfirm'));
    if (!ok) return;
    setBulkSaving(true);
    setBulkFeedback(null);
    try {
      const res = await mvpApi.bulkAssignUnassignedCases(Number(bulkAdvisorSelect));
      const n = res.data?.updated_count ?? 0;
      setBulkFeedback(n === 1 ? t('advisorPortal.topNavBulkUpdatedOne') : t('advisorPortal.topNavBulkUpdatedMany', { count: n }));
    } catch (e) {
      const d = e.response?.data?.detail;
      setBulkFeedback(typeof d === 'string' ? d : t('advisorPortal.topNavBulkAssignFailed'));
    } finally {
      setBulkSaving(false);
    }
  }, [bulkAdvisorSelect, t]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeMenu();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!advisorsOpen && !portalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (portalOpen) closePortalModal();
        else closeAdvisorsModal();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [advisorsOpen, portalOpen, closeAdvisorsModal, closePortalModal]);

  const partnerBlock = selectedUser && String(selectedUser.role || '').toLowerCase() === 'partner';

  return (
    <nav className="h-14 bg-slate-900 flex items-center min-w-0 w-full max-w-[100vw] gap-2 px-3 sm:gap-4 sm:px-4 md:gap-6 md:px-6 sticky top-0 z-50">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        <BrandLockup
          containerClassName="flex items-center gap-2 sm:gap-3 min-w-0"
          textWrapperClassName="flex flex-col leading-none gap-[3px] min-w-0 overflow-hidden"
          titleClassName="max-w-[7.25rem] truncate font-bold text-white uppercase tracking-wide sm:max-w-[10rem] sm:tracking-widest sm:text-[13px] lg:max-w-none text-[11px] leading-tight"
          subtitleClassName="hidden lg:block font-semibold uppercase tracking-widest whitespace-nowrap text-[9px] text-[#f1bf00]"
          title="Global Mobility"
          subtitle={t('advisorPortal.brandSubtitle')}
        />
      </div>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-3 shrink-0 relative" ref={wrapRef}>
        <div
          className="advisor-topnav-lang notranslate shrink-0 [&_.lang-switch>button]:border-slate-600 [&_.lang-switch>button]:text-slate-300 [&_.lang-switch>button:hover]:bg-slate-800 [&_.lang-switch>button:hover]:text-white [&_.lang-switch>button.active]:border-emerald-500/50 [&_.lang-switch>button.active]:bg-emerald-950/40 [&_.lang-switch>button.active]:text-emerald-200"
          translate="no"
        >
          <LanguageSwitch />
        </div>
        <button
          type="button"
          className="flex items-center gap-0 sm:gap-2 pl-2 sm:pl-3 border-l border-slate-700 text-left cursor-pointer rounded-r-md pr-1 py-1 hover:bg-slate-800/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label={t('advisorPortal.menuAriaLabel', { name: advisorName })}
        >
          <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-semibold shrink-0">
            {initials || 'A'}
          </div>
          <div className="min-w-0 hidden sm:block">
            <p className="text-white text-xs font-medium leading-none truncate max-w-[140px] sm:max-w-[200px]">{advisorName}</p>
            <p className="text-slate-500 text-[10px] leading-none mt-0.5">{t('advisorPortal.roleAdvisor')}</p>
          </div>
          <i className={`hidden sm:block ri-arrow-down-s-line text-slate-400 text-sm shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`} aria-hidden />
        </button>

        {menuOpen ? (
          <div
            className="absolute right-0 top-full mt-1 py-1 min-w-[200px] max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg border border-slate-200 z-[60]"
            role="menu"
          >
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-3 sm:py-2.5 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-2 touch-manipulation"
              onClick={openAdvisorsModal}
            >
              <i className="ri-team-line text-slate-500" aria-hidden />
              {t('advisorPortal.menuShowAdvisors')}
            </button>
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-3 sm:py-2.5 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-2 touch-manipulation"
              onClick={openPortalModal}
            >
              <i className="ri-settings-3-line text-slate-500" aria-hidden />
              {t('advisorPortal.menuPortalSettings')}
            </button>
            <button
              type="button"
              role="menuitem"
              className="w-full text-left px-3 py-3 sm:py-2.5 text-sm text-slate-800 hover:bg-slate-50 flex items-center gap-2 touch-manipulation"
              onClick={handleLogout}
            >
              <i className="ri-logout-box-r-line text-slate-500" aria-hidden />
              {t('advisorPortal.menuLogOut')}
            </button>
          </div>
        ) : null}
      </div>

      {advisorsOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4 bg-slate-900/50 overflow-y-auto overscroll-contain"
          role="dialog"
          aria-modal="true"
          aria-labelledby="advisors-modal-title"
          onClick={closeAdvisorsModal}
        >
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl border border-slate-200 max-w-lg w-full max-h-[min(90dvh,520px)] sm:max-h-[min(80vh,520px)] flex flex-col my-0 sm:my-auto min-h-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <h2 id="advisors-modal-title" className="text-base font-semibold text-slate-900">
                  {t('advisorPortal.topNavAdvisorsTitle')}
                </h2>
                <p className="text-[11px] text-slate-500 mt-1">{t('advisorPortal.topNavAdvisorsSubtitle')}</p>
              </div>
              <button
                type="button"
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 cursor-pointer shrink-0"
                onClick={closeAdvisorsModal}
                aria-label={t('advisorPortal.topNavCloseAria')}
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {advisorsLoading ? (
                <SkeletonModal rows={4} />
              ) : advisorsError ? (
                <p className="text-sm text-red-600">{advisorsError}</p>
              ) : advisorsList.length === 0 ? (
                <p className="text-sm text-slate-500">{t('advisorPortal.topNavNoAdmins')}</p>
              ) : (
                <ul className="space-y-3">
                  {advisorsList.map((a) => {
                    const isSelf = currentAdvisorUserId != null && Number(a.id) === Number(currentAdvisorUserId);
                    const busy = deletingAdvisorId != null && Number(deletingAdvisorId) === Number(a.id);
                    return (
                      <li
                        key={a.id}
                        className="text-sm border border-slate-100 rounded-lg px-3 py-2.5 bg-slate-50/80 flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900">{a.name || a.email || `User #${a.id}`}</p>
                          {a.email ? <p className="text-slate-600 mt-0.5 break-all">{a.email}</p> : null}
                          {a.phone ? <p className="text-slate-500 text-xs mt-0.5">{a.phone}</p> : null}
                          {isSelf ? <p className="text-[11px] text-slate-400 mt-1">{t('advisorPortal.topNavYou')}</p> : null}
                        </div>
                        {!isSelf ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleDeleteAdvisor(a)}
                            className="shrink-0 text-xs font-medium text-red-600 hover:text-red-700 px-2 py-1.5 rounded-md border border-red-200 hover:bg-red-50 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed whitespace-nowrap"
                          >
                            {busy ? t('advisorPortal.topNavDeleting') : t('advisorPortal.topNavDelete')}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {portalOpen ? (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center sm:items-center p-0 sm:p-4 bg-slate-900/50 overflow-y-auto overscroll-contain"
          role="dialog"
          aria-modal="true"
          aria-labelledby="portal-modal-title"
          onClick={closePortalModal}
        >
          <div
            className="bg-white rounded-t-xl sm:rounded-xl shadow-xl border border-slate-200 max-w-xl w-full h-auto max-h-[90vh] flex flex-col my-0 sm:my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
              <h2 id="portal-modal-title" className="text-base font-semibold text-slate-900">
                {t('advisorPortal.topNavPortalModalTitle')}
              </h2>
              <button
                type="button"
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 cursor-pointer"
                onClick={closePortalModal}
                aria-label={t('advisorPortal.topNavCloseAria')}
              >
                <i className="ri-close-line text-xl" aria-hidden />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-8 pb-24">
              {portalLoading ? (
                <SkeletonModal rows={5} />
              ) : portalErr ? (
                <p className="text-sm text-red-600">{portalErr}</p>
              ) : (
                <>
                  <section>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('advisorPortal.topNavRoleSectionTitle')}</h3>
                    <p className="text-xs text-slate-500 mb-3">{t('advisorPortal.topNavRoleSectionDesc')}</p>
                    <SearchablePicker
                      options={registrations}
                      value={selectedUserId}
                      onChange={(id) => {
                        setSelectedUserId(id);
                        setRoleFeedback(null);
                      }}
                      placeholder={t('advisorPortal.topNavSelectUser')}
                      labelFunc={formatRegistrationSelectLabel}
                      searchPlaceholder={t('advisorPortal.topNavRoleFilterPlaceholder')}
                      noResultsText={t('advisorPortal.topNavNoUsersFound')}
                    />
                    {selectedUser ? (
                      <p className="text-[11px] text-slate-500 mb-2 break-all">
                        {t('advisorPortal.topNavSelectedLine', {
                          id: selectedUser.id,
                          email: selectedUser.email ? ` · ${selectedUser.email}` : '',
                          phone: selectedUser.phone ? ` · ${selectedUser.phone}` : '',
                        })}
                      </p>
                    ) : null}
                    {partnerBlock ? (
                      <p className="text-xs text-amber-700 mb-2">{t('advisorPortal.topNavPartnerNote')}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={!selectedUserId || partnerBlock || roleSaving}
                        onClick={() => applyRole('user')}
                        className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                      >
                        {t('advisorPortal.topNavSetAsCustomer')}
                      </button>
                      <button
                        type="button"
                        disabled={!selectedUserId || partnerBlock || roleSaving}
                        onClick={() => applyRole('admin')}
                        className="text-sm px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                      >
                        {t('advisorPortal.topNavSetAsAdvisor')}
                      </button>
                    </div>
                    {roleFeedback ? <p className="text-xs mt-2 text-slate-600">{roleFeedback}</p> : null}
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('advisorPortal.topNavDefaultAdvisorTitle')}</h3>
                    <p className="text-xs text-slate-500 mb-3">{t('advisorPortal.topNavDefaultAdvisorDesc')}</p>
                    <SearchablePicker
                      options={advisorsForSelect}
                      value={defaultAdvisorSelect}
                      onChange={(id) => {
                        setDefaultAdvisorSelect(id);
                        setPortalFeedback(null);
                      }}
                      placeholder={t('advisorPortal.topNavDefaultAdvisorNone')}
                      labelFunc={formatAdvisorSelectLabel}
                      searchPlaceholder={t('advisorPortal.topNavBulkSelectAdvisor')}
                      noResultsText={t('advisorPortal.topNavNoAdvisorsFound')}
                    />
                    <button
                      type="button"
                      disabled={portalSaving}
                      onClick={saveDefaultAdvisor}
                      className="text-sm px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 cursor-pointer"
                    >
                      {t('advisorPortal.topNavSaveDefault')}
                    </button>
                    {portalFeedback ? <p className="text-xs mt-2 text-slate-600">{portalFeedback}</p> : null}
                  </section>

                  <section className="border-t border-slate-100 pt-6">
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('advisorPortal.topNavBulkTitle')}</h3>
                    <p className="text-xs text-slate-500 mb-3">{t('advisorPortal.topNavBulkDesc')}</p>
                    <SearchablePicker
                      options={advisorsForSelect}
                      value={bulkAdvisorSelect}
                      onChange={(id) => {
                        setBulkAdvisorSelect(id);
                        setBulkFeedback(null);
                      }}
                      placeholder={t('advisorPortal.topNavBulkSelectAdvisor')}
                      labelFunc={formatAdvisorSelectLabel}
                      searchPlaceholder={t('advisorPortal.topNavBulkSelectAdvisor')}
                      noResultsText={t('advisorPortal.topNavNoAdvisorsFound')}
                    />
                    <button
                      type="button"
                      disabled={bulkSaving || !bulkAdvisorSelect}
                      onClick={runBulkAssign}
                      className="text-sm px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50 cursor-pointer"
                    >
                      {t('advisorPortal.topNavBulkAssign')}
                    </button>
                    {bulkFeedback ? <p className="text-xs mt-2 text-slate-600">{bulkFeedback}</p> : null}
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
}
